import React, { Suspense, useEffect } from 'react';
import './App.scss';
import {
    AppBar,
    Box,
    Container,
    CssBaseline,
    IconButton,
    Slide,
    ThemeProvider,
    Toolbar,
    useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import useIdleLogout from '@hooks/useIdleLogout';
import useStableScrollTrigger from '@hooks/useStableScrollTrigger';
import { theme } from '@features/common/theme/theme';
import useAuthentication from '@hooks/useAuthentication';
import { selectIsBoddUserImpersonatingMerchant } from '@features/common/auth/authSlice';
import LandingComponent from './features/common/components/LandingComponent/LandingComponent';
import BoddIconComponent from './features/common/components/BoddIconComponent/BoddIconComponent';
import boddVariables from './variables.module.scss';
import { useAppSelector } from './app/hooks';
import LoadingComponent from './features/common/components/LoadingComponent/LoadingComponent';

const RoutingComponent = React.lazy(
    () => import('./features/common/navigation/RoutingComponent/RoutingComponent'),
);
const NotificationsWrapperComponent = React.lazy(
    () => import('./features/common/notifications/NotificationsWrapperComponent/NotificationsWrapperComponent'),
);
const HeaderComponent = React.lazy(
    () => import('./features/common/components/HeaderComponent/HeaderComponent'),
);
const ImpersonateBarComponent = React.lazy(
    () => import('./features/common/components/ImpersonateBarComponent/ImpersonateBarComponent'),
);

function App() {
    useAuthentication();
    useIdleLogout();
    const isBoddImpersonating = useAppSelector(selectIsBoddUserImpersonatingMerchant);
    const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const scrollTrigger = useStableScrollTrigger({});
    const drawerWidth = isMobileLayout ? 257 : 92;
    const [menuMobileOpen, setMenuMobileOpen] = React.useState(false);
    const [menuOpen, setOpen] = React.useState(false);

    useEffect(() => {
        setMenuMobileOpen(false);
    }, [location]);

    useEffect(() => {
        setOpen(menuMobileOpen || !isMobileLayout);
    }, [menuMobileOpen, isMobileLayout]);

    const containerTopMargin = () => {
        if (isMobileLayout && isBoddImpersonating) {
            return '99px';
        }
        return isMobileLayout ? '60px' : '0px';
    };

    return (
        <div className="App" data-testid="App">
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <Suspense fallback={null}>
                        {!isMobileLayout && <ImpersonateBarComponent />}
                    </Suspense>
                    <Routes>
                        <Route
                            path="/"
                            element={(
                                <LandingComponent />
                            )}
                        />
                        <Route
                            path="*"
                            element={(
                                <Suspense fallback={<LoadingComponent isLoading />}>
                                <div
                                    style={{ display: 'flex', flexDirection: 'column' }}
                                >
                                    <HeaderComponent
                                        drawerWidth={drawerWidth}
                                        open={menuOpen}
                                        onClose={() => setMenuMobileOpen(false)}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flex: 1,
                                        }}
                                    >
                                        <Slide appear={false} direction="down" in={!scrollTrigger}>
                                            <AppBar
                                                position="fixed"
                                                elevation={0}
                                                sx={(t) => ({
                                                    bgcolor: boddVariables.white,
                                                    height: isBoddImpersonating ? '99px' : '60px',
                                                    borderBottom: 1,
                                                    borderColor: boddVariables.grey50,
                                                    display: { xs: 'flex', sm: 'flex', md: 'none' },
                                                    marginLeft: `${menuOpen ? drawerWidth : 0}px`,
                                                    width: `calc(100% - ${menuOpen ? drawerWidth : 0}px)`,
                                                    overflow: 'hidden',
                                                    left: 0,
                                                    p: 0,
                                                    [theme.breakpoints.down('md')]: {
                                                        padding: theme.spacing(0),
                                                    },
                                                    transition:
                                                        t.transitions.create(['margin', 'width'], {
                                                            easing: menuOpen ? t.transitions.easing.easeOut : t.transitions.easing.sharp,
                                                            duration: menuOpen ? t.transitions.duration.enteringScreen : t.transitions.duration.leavingScreen,
                                                        }),
                                                })}
                                            >
                                                <ImpersonateBarComponent />
                                                <Toolbar sx={{ justifyContent: 'space-between' }}>
                                                    <IconButton
                                                        onClick={() => setMenuMobileOpen(!menuOpen)}
                                                    >
                                                        <MenuIcon style={{ color: boddVariables.boddBlack }} />
                                                    </IconButton>
                                                    <BoddIconComponent
                                                        clicked={() => navigate('/')}
                                                    />
                                                    <Box sx={{ width: 24 }} />
                                                </Toolbar>
                                            </AppBar>
                                        </Slide>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                flex: 1,
                                            }}
                                        >
                                            <div style={{ width: isMobileLayout ? 0 : drawerWidth }} />
                                            <Container
                                                maxWidth="xl"
                                                sx={(t) => ({
                                                    display: 'flex',
                                                    flex: 1,
                                                    padding: { xs: 0 },
                                                    width: `calc(100% - ${drawerWidth}px)`,
                                                    marginTop: containerTopMargin(),
                                                    transition:
                                                        t.transitions.create(['margin', 'width'], {
                                                            easing: menuOpen ? t.transitions.easing.easeOut : t.transitions.easing.sharp,
                                                            duration: menuOpen ? t.transitions.duration.enteringScreen : t.transitions.duration.leavingScreen,
                                                        }),
                                                })}
                                            >
                                                <Box sx={{
                                                    flexGrow: 1,
                                                    p: 3,
                                                    width: { xs: `calc(100% - ${drawerWidth}px)` },
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    padding: { xs: 0 },
                                                    marginRight: '20px',
                                                    marginLeft: '20px',
                                                    marginTop: 0,
                                                }}
                                                >
                                                    <NotificationsWrapperComponent />
                                                    <RoutingComponent />
                                                </Box>
                                            </Container>
                                        </div>
                                    </div>
                                </div>
                                </Suspense>
                            )}
                        />
                    </Routes>
                </ThemeProvider>
            </LocalizationProvider>
        </div>
    );
}

export default App;
